import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import toast from 'react-hot-toast';

const NotificationContext = createContext();

const POLL_INTERVAL_MS = 15000;

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestNotification, setLatestNotification] = useState(null);
  const lastSeenIdRef = useRef(null);

  const pollNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    try {
      const params = lastSeenIdRef.current != null ? { since: lastSeenIdRef.current } : {};
      const response = await api.get('/notifications/notifications/poll/', { params });
      const { unread_count, latest_id, new_notifications } = response.data || {};

      setUnreadCount(unread_count || 0);

      if (Array.isArray(new_notifications) && new_notifications.length > 0) {
        // Backend returns newest-first; surface in chronological order so the
        // most recent toast lands last and `latestNotification` ends correct.
        const chronological = [...new_notifications].reverse();
        chronological.forEach((notification) => {
          setLatestNotification(notification);
          if (notification.message) {
            toast(notification.message, { icon: '🔔', duration: 5000 });
          }
          window.dispatchEvent(new CustomEvent('newNotification', { detail: notification }));
        });
      }

      if (typeof latest_id === 'number') {
        lastSeenIdRef.current = latest_id;
      }
    } catch (err) {
      setUnreadCount(0);
    }
  }, [isAuthenticated]);

  // Poll loop — replaces the prior SSE EventSource connection. Pauses when the
  // tab is hidden so background tabs don't waste backend slots or mobile battery,
  // and fires an immediate poll on re-focus so the badge updates without waiting
  // out the next tick.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setUnreadCount(0);
      setLatestNotification(null);
      lastSeenIdRef.current = null;
      return;
    }

    let intervalId = null;

    const start = () => {
      if (intervalId != null) return;
      pollNotifications();
      intervalId = setInterval(pollNotifications, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalId == null) return;
      clearInterval(intervalId);
      intervalId = null;
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stop();
    };
  }, [isAuthenticated, user, pollNotifications]);

  useEffect(() => {
    const handleNotificationsMarkedRead = () => {
      setUnreadCount(0);
      pollNotifications();
    };

    window.addEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);
    return () => {
      window.removeEventListener('notificationsMarkedRead', handleNotificationsMarkedRead);
    };
  }, [pollNotifications]);

  const value = {
    unreadCount,
    latestNotification,
    refreshUnreadCount: pollNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

